import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  FormControl,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  StoreUtil,
} from 'doctivity-shared/utils';
import { TagsUtil } from 'utils';
import {
  listTagNamespacesByClient,
} from 'store/actions/tagNamespacesActions';

const styles = (theme) => ({
  filterDropDown: {
    width: 300,
    marginRight: theme.spacing(2),
  },
  groupHeader: {
    position: 'sticky',
    top: -8,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.primary.main,
    backgroundColor: '#fff',
  },
  groupList: {
    padding: 0,
  },
});

/**
 * "Label" filter for the Visits & Referrals page: a searchable Autocomplete of this
 * client's labels, grouped under their label type, limited to label types enabled for
 * providers (`providers_drop_down`). Value is a tag id, or null for "All". Reuses the
 * cached API_TAG_NAMESPACES_LIST_BY_CLIENT store that TagSelector / MarketOrTagSelect use.
 */
class ProviderLabelSelect extends React.Component {
  componentDidMount() {
    this.checkState();
  }

  componentDidUpdate() {
    this.checkState();
  }

  checkState() {
    if (
      StoreUtil.needsLoadLongCache(this.props.tagNamespaces)
      || this.props.tagNamespaces.clientId !== this.props.clientId
    ) {
      this.fetchData();
    } else if (
      this.props.value
      && StoreUtil.isLoaded(this.props.tagNamespaces)
      && !this.findSelectableTag(this.props.value)
    ) {
      // The selected label is not one of this client's provider labels (the user switched
      // clients, or the label was deleted / had providers disabled). Fall back to All so
      // the page doesn't keep sending a tag_id the backend's tenant guard will reject.
      this.props.onChange(null);
    }
  }

  findSelectableTag(tagId) {
    const namespaces = TagsUtil.getNamespacesByType('providers', this.props.tagNamespaces);
    return namespaces.some((ns) => (ns.tags || []).some((tag) => tag.id === tagId));
  }

  fetchData() {
    this.props.dispatch(listTagNamespacesByClient(this.props.clientId));
  }

  getOptions() {
    const options = [];
    if (StoreUtil.isLoaded(this.props.tagNamespaces)) {
      TagsUtil.getNamespacesByType('providers', this.props.tagNamespaces).forEach((ns) => {
        (ns.tags || []).forEach((tag) => {
          options.push({ id: tag.id, name: tag.name, type: ns.name });
        });
      });
    }
    return options;
  }

  render() {
    const {
      classes,
    } = this.props;

    const options = this.getOptions();
    const value = options.find((o) => o.id === this.props.value) || null;

    return (
      <FormControl variant='outlined' className={classes.filterDropDown} key='filter-label'>
        <Autocomplete
          options={options}
          autoComplete
          groupBy={(o) => o.type}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={value}
          onChange={this.onChange}
          renderGroup={(params) => (
            <li key={params.key}>
              <div className={classes.groupHeader}>{params.group}</div>
              <ul className={classes.groupList}>{params.children}</ul>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label='Label' placeholder='All' />
          )}
        />
      </FormControl>
    );
  }

  onChange = (event, newValue) => {
    this.props.onChange(newValue ? newValue.id : null);
  };
}

ProviderLabelSelect.propTypes = {
  classes: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  tagNamespaces: PropTypes.object,
  value: PropTypes.number,
  clientId: PropTypes.number,
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    tagNamespaces: StoreUtil.get(
      state.tagNamespaces,
      'API_TAG_NAMESPACES_LIST_BY_CLIENT',
    ),
  };
}

const styled = withStyles(styles)(ProviderLabelSelect);
const connected = connect(mapStateToProps)(styled);
export { connected as ProviderLabelSelect };
